import { DEFAULT_ASSIGNMENT_ID, DEFAULT_DRAFT_HTML } from "./constants.js";
import { MAX_MAJOR_GROWTH_EVENTS, MAX_METRIC_VALUE } from "./limits.js";
import { countWords, htmlToStructuredText } from "../util/text.js";
import type {
  Draft,
  DraftVersion,
  MajorGrowthEvent,
  SaveMode,
  WritingEvent,
  WritingEventType,
  WritingMetrics,
} from "../types.js";

export function createMetrics(text: string, now: string): WritingMetrics {
  return {
    sessionStartTime: now,
    sessionEndTime: null,
    totalElapsedMs: 0,
    activeWritingMs: 0,
    idleMs: 0,
    keystrokeCount: 0,
    totalCharacters: text.length,
    wordCount: countWords(text),
    pasteEvents: 0,
    pastedCharacters: 0,
    pastedWords: 0,
    deletionCount: 0,
    focusEvents: 0,
    blurEvents: 0,
    revisionBursts: 0,
    reopenSessions: 0,
    majorGrowthEvents: [],
    finalSubmissionTime: null,
  };
}

/**
 * Completed enter+leave cycles: one entrance and one exit is one focus change.
 *
 * A round trip needs both halves, so an in-progress visit that has not been
 * closed by a matching leave does not count; pairing the two counters with
 * `min` enforces that. Kept here as the single shared definition so the
 * browser tile and the PDF row cannot drift.
 */
export function focusRoundTrips(metrics: WritingMetrics): number {
  return Math.min(metrics.focusEvents, metrics.blurEvents);
}

export function createDraft(html: string = DEFAULT_DRAFT_HTML, now = new Date().toISOString()): Draft {
  const text = htmlToStructuredText(html);
  return {
    assignmentId: DEFAULT_ASSIGNMENT_ID,
    html,
    text,
    currentSubmissionId: null,
    professorEmail: "",
    studentEmail: "",
    lastSavedAt: null,
    activeVersionSavedAt: null,
    saveMode: "draft",
    trackingActive: false,
    metrics: createMetrics(text, now),
    versions: [],
    events: [],
    statusNotes: [],
    activity: {
      isFocused: false,
      lastActivityAt: null,
      lastInputAt: null,
      revisionWindowStart: null,
      revisionWindowChanges: 0,
      hasSessionOpened: false,
    },
  };
}

const NUMBER_KEYS = [
  "totalElapsedMs",
  "activeWritingMs",
  "idleMs",
  "keystrokeCount",
  "totalCharacters",
  "wordCount",
  "pasteEvents",
  "pastedCharacters",
  "pastedWords",
  "deletionCount",
  "focusEvents",
  "blurEvents",
  "revisionBursts",
  "reopenSessions",
] as const satisfies ReadonlyArray<keyof WritingMetrics>;

function toNumber(value: unknown): number {
  const parsed = Number(value ?? 0);
  // Non-finite and negative sources repair to zero; an oversized (tampered or
  // legacy) value repairs to the shared ceiling rather than being quarantined,
  // mirroring the schema's clamp.
  return Number.isFinite(parsed) ? Math.min(MAX_METRIC_VALUE, Math.max(0, parsed)) : 0;
}

/**
 * Coerces a persisted (or client-supplied) timestamp to a parseable string.
 * Legacy timestamps predate ISO strings and can carry epoch milliseconds — a
 * finite numeric source is converted to an ISO string because
 * `String(1720000000000)` is "1720000000000", which `new Date(...)` cannot
 * parse, and a numeric-STRING epoch survives the same way. Values outside
 * `new Date`'s ±8.64e15 ms range (a sentinel/corruption artifact) cannot be
 * rendered as ISO and fall back to the original string rather than throwing
 * `RangeError` out of hydrate. Any other value is stringified as before, and a
 * falsy or non-finite source keeps its caller's fallback (null / now).
 */
function coerceTimestamp(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return toIsoTimestampOrOriginal(value);
  }
  if (typeof value === "string" && /^-?\d+$/.test(value)) {
    return toIsoTimestampOrOriginal(Number(value));
  }
  return String(value);
}

function toIsoTimestampOrOriginal(value: number): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
}

/**
 * True when a source is a usable timestamp for an array-entry gate: a
 * non-empty string (a legacy all-digit epoch string is coerced later) or a
 * finite positive number. Falsy sources (0, "", null, undefined) mean
 * "absent" exactly as they do on the object-level time fields, and a
 * non-finite number (NaN, ±Infinity) would otherwise render as the literal
 * strings "NaN"/"Infinity", so both join the caller's drop path.
 */
function isUsableTimestamp(value: unknown): value is string | number {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0;
  }
  return typeof value === "string" && value.length > 0;
}

/**
 * Whitelist constructor: every key of `WritingMetrics` must be handled here in
 * the same change that adds it, or the field is silently dropped on the next
 * sync.
 *
 * Coerces any persisted (or client-supplied) metrics object into a full record.
 */
export function normalizeMetrics(
  input: Partial<WritingMetrics> | undefined,
  now = new Date().toISOString(),
): WritingMetrics {
  const source = input ?? {};
  const metrics = createMetrics("", now);

  for (const key of NUMBER_KEYS) {
    metrics[key] = toNumber(source[key]);
  }

  // Legacy timestamps predate ISO strings; coerce any truthy value to a
  // parseable string so a numeric field never survives into the typed shape.
  metrics.sessionStartTime = source.sessionStartTime ? coerceTimestamp(source.sessionStartTime) : now;
  metrics.sessionEndTime = source.sessionEndTime ? coerceTimestamp(source.sessionEndTime) : null;
  metrics.finalSubmissionTime = source.finalSubmissionTime
    ? coerceTimestamp(source.finalSubmissionTime)
    : null;
  metrics.majorGrowthEvents = Array.isArray(source.majorGrowthEvents)
    ? source.majorGrowthEvents
        .map(normalizeMajorGrowthEvent)
        .filter((event): event is MajorGrowthEvent => event !== null)
        .slice(0, MAX_MAJOR_GROWTH_EVENTS)
    : [];

  return metrics;
}

/**
 * Coerces a major-growth entry's timestamp the same way as every other time
 * field (numeric epochs and numeric-STRING epochs become ISO, out-of-range
 * sentinels survive as strings) and coerces its numeric fields, mirroring the
 * version and event repair: a malformed entry that would be read back into the
 * PDF and review is either fixed or dropped, never passed through unchecked.
 * A falsy or non-finite timestamp source is dropped, not stringified.
 */
function normalizeMajorGrowthEvent(
  event: Partial<MajorGrowthEvent> | null | undefined,
): MajorGrowthEvent | null {
  if (!event || !isUsableTimestamp(event.timestamp)) {
    return null;
  }
  return {
    timestamp: coerceTimestamp(event.timestamp),
    charsAdded: toNumber(event.charsAdded),
    paragraphIndex: toNumber(event.paragraphIndex),
  };
}

function normalizeVersion(version: Partial<DraftVersion> | undefined): DraftVersion | null {
  if (
    !version ||
    !isUsableTimestamp(version.savedAt) ||
    typeof version.html !== "string" ||
    typeof version.text !== "string"
  ) {
    return null;
  }
  return {
    savedAt: coerceTimestamp(version.savedAt),
    html: version.html,
    text: version.text,
    metrics: {
      wordCount: toNumber(version.metrics?.wordCount),
      totalCharacters: toNumber(version.metrics?.totalCharacters),
      keystrokeCount: toNumber(version.metrics?.keystrokeCount),
    },
  };
}

/** The closed set of types the editor records, mirroring `WritingEventType`. */
const WRITING_EVENT_TYPES: ReadonlySet<WritingEventType> = new Set([
  "session_start",
  "session_reopen",
  "text_input",
  "major_growth",
  "revision_burst",
  "paste",
  "focus",
  "blur",
  "draft_restore",
  "submit",
]);

/**
 * Drops events that lack a usable timestamp (a non-empty string or a finite
 * positive number — falsy and non-finite sources such as 0, "", NaN, or
 * ±Infinity would otherwise survive as literal strings), lack a string type,
 * or carry a type outside the `WritingEventType` union, and coerces a
 * non-object payload to `{}`, mirroring the version repair: a legacy event
 * that would be served forever is either fixed or dropped, never passed
 * through unchecked.
 */
export function normalizeEvent(event: Partial<WritingEvent> | null | undefined): WritingEvent | null {
  if (
    !event ||
    typeof event.type !== "string" ||
    !WRITING_EVENT_TYPES.has(event.type as WritingEventType) ||
    !isUsableTimestamp(event.timestamp)
  ) {
    return null;
  }
  return {
    type: event.type as WritingEventType,
    timestamp: coerceTimestamp(event.timestamp),
    payload:
      typeof event.payload === "object" && event.payload !== null && !Array.isArray(event.payload)
        ? event.payload
        : {},
  };
}

/**
 * Whitelist constructor: every key of `Draft` must be handled here in the same
 * change that adds it, or the field is silently dropped on the next sync.
 *
 * Rebuilds a complete `Draft` from partial or legacy data. This is the
 * migration path for the prototype's `localStorage` payload and the guard
 * against a malformed draft sync from a client.
 */
export function normalizeDraft(
  draft: Partial<Draft> | null | undefined,
  now = new Date().toISOString(),
): Draft {
  if (!draft) {
    return createDraft(DEFAULT_DRAFT_HTML, now);
  }

  const html = typeof draft.html === "string" ? draft.html : DEFAULT_DRAFT_HTML;

  return {
    assignmentId: draft.assignmentId || DEFAULT_ASSIGNMENT_ID,
    html,
    text: typeof draft.text === "string" ? draft.text : htmlToStructuredText(html),
    currentSubmissionId: draft.currentSubmissionId || null,
    professorEmail: typeof draft.professorEmail === "string" ? draft.professorEmail : "",
    studentEmail: typeof draft.studentEmail === "string" ? draft.studentEmail : "",
    lastSavedAt: draft.lastSavedAt ? coerceTimestamp(draft.lastSavedAt) : null,
    activeVersionSavedAt: draft.activeVersionSavedAt ? coerceTimestamp(draft.activeVersionSavedAt) : null,
    saveMode: ["draft", "manual", "autosave"].includes(draft.saveMode as string)
      ? (draft.saveMode as SaveMode)
      : "draft",
    trackingActive: Boolean(draft.trackingActive),
    metrics: normalizeMetrics(draft.metrics, now),
    versions: Array.isArray(draft.versions)
      ? draft.versions.map(normalizeVersion).filter((v): v is DraftVersion => v !== null)
      : [],
    events: Array.isArray(draft.events)
      ? draft.events.map(normalizeEvent).filter((e): e is WritingEvent => e !== null)
      : [],
    statusNotes: Array.isArray(draft.statusNotes) ? draft.statusNotes : [],
    activity: {
      isFocused: Boolean(draft.activity?.isFocused),
      lastActivityAt: draft.activity?.lastActivityAt
        ? coerceTimestamp(draft.activity.lastActivityAt)
        : null,
      lastInputAt: draft.activity?.lastInputAt ? coerceTimestamp(draft.activity.lastInputAt) : null,
      revisionWindowStart: draft.activity?.revisionWindowStart
        ? coerceTimestamp(draft.activity.revisionWindowStart)
        : null,
      revisionWindowChanges: toNumber(draft.activity?.revisionWindowChanges),
      hasSessionOpened: Boolean(draft.activity?.hasSessionOpened),
    },
  };
}

/**
 * Starts a fresh draft while carrying forward the assignment, the professor and
 * student email addresses, and the Draft Library, exactly as the prototype did
 * after a submit or a reset.
 */
export function createSuccessorDraft(
  previous: Draft,
  versions: DraftVersion[],
  now = new Date().toISOString(),
): Draft {
  const next = createDraft(DEFAULT_DRAFT_HTML, now);
  next.assignmentId = previous.assignmentId;
  next.professorEmail = previous.professorEmail;
  next.studentEmail = previous.studentEmail;
  next.versions = versions;
  return next;
}
